import type { HttpCore } from '../http';
import type { OAuthStartRequest, OAuthStartResponse, ProviderDescriptor } from '../types';

/** `/api/v1/providers` — anonymous provider descriptors that drive the "add inverter" form. */
export function createProvidersApi(http: HttpCore) {
  return {
    /** The API wraps the list: `{ providers: ProviderDescriptor[] }`. */
    list: () =>
      http.get<{ providers: ProviderDescriptor[] }>('/providers', { auth: false }).then((r) => r.providers ?? []),
    /**
     * Starts the consent flow of an `authType: 'oauth'` provider (customer JWT). Send the browser
     * to `authorizeUrl`; the API redirects it back to `returnPath` with `?connection=` or
     * `?oauthError=`. Fails with `provider_not_configured` (503) when SolarFox has no app
     * credentials for the brand, or `provider_not_oauth` (400) for a form-based provider.
     */
    oauthStart: (code: string, body: OAuthStartRequest = {}) =>
      http.post<OAuthStartResponse>(`/providers/${encodeURIComponent(code)}/oauth/start`, body),
    /**
     * "My brand is not listed" (customer JWT): records the inverter brand the customer typed, so the
     * next integration is chosen by demand. 204 on success; `validation_error` (400) for an empty or
     * over-long brand, `rate_limited` (429) when the same account keeps sending.
     */
    requestBrand: (brand: string) => http.post<void>('/providers/requests', { brand }),
  };
}

export type ProvidersApi = ReturnType<typeof createProvidersApi>;
